import { AxiosResponse } from "axios";
import { AxiosClient } from "../../utils/AxiosClient";
import { baseUrl } from "../apiUrl";
import { IPatchRequest } from "../common/patch";
import { IBuyer, IDisplayVals, IHomesite } from "./homesites.types";

/**
 * Filters the buyers array to include only those with a role that contains 'buyer'.
 * @param {IBuyer[]} buyers - Array of buyers to filter
 * @returns {IBuyer[]} Filtered array of buyers who have a role containing 'buyer'
 */
const filterOnlyBuyers = (buyers: IBuyer[]): IBuyer[] =>
  buyers?.filter(buyer => buyer?.role?.toLowerCase().includes('buyer')) || [];

/**
 * Merges the names of buyers into a single string.
 * @param {IBuyer[]} buyers - Array of buyers whose names to merge
 * @param {string} separator - Character to separate multiple names (default: ',')
 * @returns {string} Concatenated names with the specified separator
 */
const mergeNames = (buyers: IBuyer[], separator: string = ','): string =>
  buyers
    .map(buyer =>
      [buyer?.firstName, buyer?.middleName, buyer?.lastName]
        .filter(Boolean)
        .join(" ")
    )
    .filter(Boolean)
    .join(separator);

/**
 * Merges values from a specific field across all buyers into a single string.
 * @param {IBuyer[]} buyers - Array of buyers whose field values to merge
 * @param {keyof IBuyer} field - The field name to extract from each buyer
 * @param {string} separator - Character to separate multiple values (default: ', ')
 * @returns {string} Concatenated field values with the specified separator.
 */
const mergeValue = (buyers: IBuyer[], field: keyof IBuyer, separator: string = ', '): string =>
  buyers
    .map(buyer => buyer?.[field])
    .filter(Boolean)
    .join(separator);

/**
 * Generates display values for a list of buyers by filtering and merging their information.
 * Filters out non-buyer roles and creates formatted strings for name, email, and phone.
 * @param {IBuyer[] | undefined} buyers - Array of buyers to process, can be undefined
 * @returns {IDisplayVals} Object containing formatted name, email, and phone strings
 */
export function getDisplayVals(buyers: IBuyer[] | undefined): IDisplayVals {
  const filteredBuyers = filterOnlyBuyers(buyers ?? []);
  return {
    name: mergeNames(filteredBuyers),
    email: mergeValue(filteredBuyers, "email", "; "),
    phone: mergeValue(filteredBuyers, "phone"),
  };
}

/**
 * Sorts buyers array by primary status and role.
 * Primary buyers are sorted first, followed by non-primary buyers sorted by role.
 * @param {IBuyer[]} buyers - Array of buyers to sort
 * @returns {IBuyer[]} Sorted array of buyers with primary buyers first, then sorted by role
 */
const sortBuyers = (buyers: IBuyer[]): IBuyer[] => {
  return buyers?.sort((a, b) => {
    // Primary buyers get highest priority (sorted first)
    if (a.primary === true) return -1;
    if (b.primary === true) return 1;
    // For non-primary buyers, sort by role alphabetically
    return (a.role ?? "").localeCompare(b.role ?? "") || 0;
  }) || [];
};

/**
 * Processes a homesite by sorting buyers and generating display values
 * @param {IHomesite} homesite - The homesite to process
 * @returns {IHomesite} The processed homesite with sorted buyers and display values
 */
export const processHomesite = (homesite: IHomesite): IHomesite => {
  homesite.buyers = sortBuyers(homesite.buyers ?? []);
  homesite.displayVals = getDisplayVals(homesite.buyers);
  return homesite;
};

/**
 * Get the homesites for a division.
 * @param {string} divisionCode - three letter divisionCode. Required if conciergeId not provided.
 * @returns The service response for the homesites, so if it has errors it can be handled wherever it's used
 */
export const getHomesites = async (divisionCode: string): Promise<IHomesite[]> => {
  const service = baseUrl + `/clo/homesites?divisioncode=${divisionCode}&docStatus=true`;
  const response: AxiosResponse<IHomesite[]> = await AxiosClient.get<IHomesite[]>(service);
  return response.data.map(processHomesite);
};


/**
 * Get a specific homesite by its ID for a given division.
 * @param {string} divisionCode - Three letter division code
 * @param {string} homesiteId - The unique identifier of the homesite
 * @returns {Promise<IHomesite>} The processed homesite with sorted buyers and display values
 */
export const getHomeSiteById = async (divisionCode: string, homesiteId: string) => {
  const service = baseUrl + `/clo/homesites/${homesiteId}?divisioncode=${divisionCode}&docStatus=true`;
  const response: AxiosResponse<IHomesite> = await AxiosClient.get<IHomesite>(service);
  return processHomesite(response.data);
};

/**
 * Update a homesite using PATCH request with the provided changes.
 * @param {string} divisionCode - Three letter division code
 * @param {string} homeSiteId - The unique identifier of the homesite to update
 * @param {IPatchRequest} body - The patch request containing the changes to apply
 * @returns {Promise<IHomesite>} The updated and processed homesite with sorted buyers and display values
 */
export const patchHomesite = async (divisionCode: string, homeSiteId: string, body: IPatchRequest) => {
  const service = baseUrl + `/clo/homesites/${homeSiteId}?divisioncode=${divisionCode}`;
  const response: AxiosResponse<IHomesite> = await AxiosClient.patch(service, body);
  return processHomesite(response.data);
};
